import { FieldValues } from "react-hook-form";

/** A dot-path field (e.g. BDI-3's "Total Screening Score.Raw Score") registers
 *  into react-hook-form's internal state even when its parent object is
 *  `null` -- so typing into and clearing a single sub-field materializes the
 *  whole parent into an object of all-null/empty values, even though the
 *  user's net edit was a no-op. Restores `null` for any top-level field that
 *  started `null` in `originalValues` and comes back as an object with
 *  nothing actually filled in, so a stray touch-and-clear can't turn a
 *  single-domain report's absent Total Screening Score into a persisted
 *  empty one. Only ever restores a field to a state (`null`) it already
 *  validated as before submission -- never introduces a new value the schema
 *  hasn't already accepted. */
export function restoreNullForUntouchedNestedObjects<T extends FieldValues>(data: T, originalValues: T): T {
  const result: Record<string, unknown> = { ...data };

  for (const key of Object.keys(result)) {
    if ((originalValues as Record<string, unknown>)[key] !== null) continue;

    const value = result[key];
    if (value === null || typeof value !== "object" || Array.isArray(value)) continue;

    const isEmpty = Object.values(value as Record<string, unknown>).every(
      (v) => v === null || v === undefined || v === "",
    );
    if (isEmpty) {
      result[key] = null;
    }
  }

  return result as T;
}
